import { Link } from "react-router-dom";
import { useAudience } from "../context/AudienceContext";
import SegmentedSwitch from "../components/SegmentedSwitch";
import Reveal, { HeadlineMask } from "../components/Reveal";
import Parallax from "../components/Parallax";
import SectionLabel from "../components/SectionLabel";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { IMAGES, STYLE_SERIES, PACKAGES, OBJECT_TYPES, PROCESS_STEPS, FAQS, AUDIENCE_COPY } from "../data/content";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export default function Home() {
  const { audience } = useAudience();
  const copy = AUDIENCE_COPY[audience];
  useDocumentTitle(
    "Schlüsselfertige Einrichtungskonzepte für Ferienobjekte",
    "Livinora plant komplette Einrichtungskonzepte für Ferienhäuser, Ferienwohnungen und Apartments — schlüsselfertig, stilsicher, ohne Reibung."
  );

  const openNora = () => window.dispatchEvent(new CustomEvent("livinora:open-nora"));

  return (
    <div data-testid="page-home">
      {/* ─────────────────────────── HERO ─────────────────────────── */}
      <section className="relative pt-28 sm:pt-32 md:pt-40 pb-20 sm:pb-24 md:pb-36 overflow-hidden" data-testid="hero">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-20 items-end">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Reveal>
              <span className="micro-label">Livinora — Interior Concepts</span>
            </Reveal>
            <Reveal delay={60}>
              <h1
                className="display-xl mt-6 sm:mt-8 text-[42px] sm:text-[58px] md:text-[72px] lg:text-[92px] text-balance"
                data-testid="hero-headline"
              >
                {copy.heroHeadline}
              </h1>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora prose-livinora--lead mt-7 sm:mt-9">
                {copy.heroSub}
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-5">
                <Link
                  to={copy.primaryHref}
                  className="btn-livinora btn-primary btn-magnetic w-full sm:w-auto justify-center sm:justify-start"
                  data-testid="hero-primary-cta"
                >
                  {copy.primaryCTA} <span>→</span>
                </Link>
                <Link
                  to={copy.secondaryHref}
                  className="btn-livinora btn-ghost w-full sm:w-auto justify-center sm:justify-start"
                  data-testid="hero-secondary-cta"
                >
                  {copy.secondaryCTA}
                </Link>
                <button
                  type="button"
                  onClick={openNora}
                  className="btn-quiet hidden sm:inline-flex"
                  data-testid="hero-quiet-cta"
                  aria-label="Nora — Livinora Concierge öffnen"
                >
                  {copy.quietCTA}
                </button>
              </div>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-10 sm:mt-14 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
                <SegmentedSwitch testId="hero-audience-switch" />
                <div className="trust-strip" aria-label="Prozess-Übersicht">
                  <span>Konzept</span>
                  <span>Stilserie</span>
                  <span>Paket</span>
                  <span>Anfrage</span>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <Reveal variant="scale" delay={120}>
              <Parallax speed={28}>
                <div className="media-wrap media-mask-reveal aspect-[5/6] sm:aspect-[4/5] noise">
                  <img
                    src={STYLE_SERIES[0].image}
                    alt="Premium Coastal Interior — Stilserie Küste"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Parallax>
            </Reveal>
            <Reveal delay={280}>
              <div className="mt-5 sm:mt-6 flex items-baseline justify-between border-t border-[color:var(--livinora-hairline)] pt-4">
                <span className="micro-label">Stilserie · Küste</span>
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-[color:var(--livinora-stone-deep)]">
                  001 / 005
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────────── 01 KONZEPT — OBJECT PATHWAYS ───────────────────── */}
      <section className="section-pad border-t border-[color:var(--livinora-hairline)]" data-testid="object-pathways">
        <div className="container-livinora">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
            <div className="md:col-span-6">
              <Reveal>
                <SectionLabel index="01" text="Konzept" />
              </Reveal>
              <Reveal delay={60}>
                <h2 className="serif text-4xl md:text-5xl lg:text-6xl mt-8 leading-[1.02] text-balance">
                  Vier Wege, ein Objekt fertig zu denken.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <Reveal delay={140}>
                <p className="prose-livinora">
                  Wir sortieren nicht nach Produktkategorien, sondern nach dem, was vor Ihnen steht:
                  ein Haus, eine Wohnung, ein Apartment — oder ein Bestand, der neu gelesen werden will.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {OBJECT_TYPES.map((o, i) => (
              <Reveal key={o.slug} variant="scale" delay={i * 100}>
                <Link
                  to={`/konzepte#${o.slug}`}
                  className="block group"
                  data-testid={`pathway-${o.slug}`}
                >
                  <div className="media-wrap aspect-[16/11]">
                    <img src={o.image} alt={o.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-6">
                    <div className="min-w-0">
                      <span className="micro-label">0{i + 1}</span>
                      <h3 className="serif text-3xl md:text-4xl mt-2 leading-tight transition-transform duration-700 group-hover:translate-x-1">
                        {o.name}
                      </h3>
                      <p className="mt-3 text-[color:var(--livinora-muted)] max-w-md leading-relaxed">{o.pitch}</p>
                    </div>
                    <span className="btn-line mt-4 shrink-0">Ansehen</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────── 02 STILSERIEN ───────────────────── */}
      <section className="section-pad bg-[color:var(--livinora-paper-warm)] border-y border-[color:var(--livinora-hairline)]" data-testid="style-teaser">
        <div className="container-livinora">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <Reveal>
                <SectionLabel index="02" text="Stilserien" />
              </Reveal>
              <Reveal delay={60}>
                <h2 className="serif text-4xl md:text-5xl lg:text-6xl mt-8 leading-[1.02] text-balance">
                  Fünf Welten. Eine Haltung.
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="prose-livinora mt-6">
                  Jede Stilserie ist eine konsequent durchgearbeitete Wohnwelt — Materialität, Palette
                  und Atmosphäre greifen ineinander. Innerhalb einer Serie verändert sich das Paket,
                  nicht die Handschrift.
                </p>
              </Reveal>
            </div>
            <Link to="/stilserien" className="btn-line" data-testid="style-teaser-all">
              Alle Stilserien
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {STYLE_SERIES.map((s, i) => (
              <Reveal key={s.slug} variant="mask" delay={i * 90}>
                <Link to={`/stilserien#${s.slug}`} className="block group" data-testid={`style-${s.slug}`}>
                  <div className="media-wrap aspect-[4/5]">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="serif text-2xl md:text-3xl leading-tight transition-transform duration-700 group-hover:translate-x-1">
                        {s.name}
                      </h3>
                      <p className="mt-1.5 text-sm text-[color:var(--livinora-muted)] tracking-wide">{s.tagline}</p>
                    </div>
                    <div className="flex gap-1 mt-2 shrink-0">
                      {s.palette.map((c, idx) => (
                        <span key={`${c}-${idx}`} className={`swatch w-3 h-3 rounded-full d-${idx + 1}`} style={{ background: c }} />
                      ))}
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────── 03 PAKETE ───────────────────── */}
      <section className="section-pad" data-testid="packages-teaser">
        <div className="container-livinora">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
            <div className="md:col-span-6">
              <Reveal>
                <SectionLabel index="03" text="Pakete" />
              </Reveal>
              <Reveal delay={60}>
                <h2 className="serif text-4xl md:text-5xl lg:text-6xl mt-8 leading-[1.02]">
                  Basis. Komfort. Premium.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <Reveal delay={140}>
                <p className="prose-livinora">
                  Drei Ausbaustufen, die sich nicht über Preis, sondern über Dichte, Materialität und
                  Aufenthaltsqualität definieren. Jede Stufe funktioniert in jeder Stilserie.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[color:var(--livinora-hairline)]">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.key} delay={i * 80}>
                <div
                  className={`package-cell p-8 md:p-10 border-b md:border-b-0 md:border-r border-[color:var(--livinora-hairline)] ${
                    i === 2 ? "md:border-r-0" : ""
                  } ${p.key === "komfort" ? "package-cell--featured" : ""} h-full flex flex-col`}
                  data-testid={`package-${p.key}`}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="micro-label">0{i + 1}</span>
                    {p.key === "komfort" && <span className="package-mark">Empfohlen</span>}
                  </div>
                  <h3 className="serif text-3xl md:text-4xl mt-3 leading-tight">{p.name}</h3>
                  <p className="mt-4 text-[color:var(--livinora-ink-soft)] font-medium leading-snug">{p.promise}</p>
                  <p className="mt-4 text-sm text-[color:var(--livinora-muted)] leading-relaxed">
                    {p.description}
                  </p>
                  <ul className="mt-8 space-y-2.5 text-sm">
                    {p.includes.map((t) => (
                      <li key={t} className="flex items-start gap-3">
                        <span className="mt-[10px] w-3 h-px bg-[color:var(--livinora-ink)]" /> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────── PROCESS ───────────────────── */}
      <section className="section-pad bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] atmosphere-dark noise" data-testid="process">
        <div className="container-livinora">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
            <div className="md:col-span-7">
              <Reveal>
                <SectionLabel index="04" text="Vorgehen" inverse />
              </Reveal>
              <Reveal delay={60}>
                <h2 className="serif text-4xl md:text-5xl lg:text-6xl mt-8 leading-[1.02]">
                  Vier Schritte vom Objekt zum Konzept.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <Reveal delay={140}>
                <p className="text-[color:var(--livinora-stone)] leading-relaxed text-base md:text-[17px]">
                  Ein klar geführter Weg — entweder selbst gesteuert über unseren Konfigurator oder
                  persönlich begleitet durch unser Planungsteam.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-[color:var(--livinora-stone-deep)]/50">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal key={step.n} delay={i * 80}>
                <div
                  className={`py-10 md:py-12 pr-6 md:pr-10 ${i !== 0 ? "md:pl-10" : ""} ${
                    i !== 3 ? "md:border-r border-[color:var(--livinora-stone-deep)]/40" : ""
                  } border-b md:border-b-0 border-[color:var(--livinora-stone-deep)]/40`}
                >
                  <div className="text-[color:var(--livinora-stone)] text-[10.5px] tracking-[0.32em]">{step.n}</div>
                  <h3 className="serif text-2xl md:text-3xl mt-3 leading-tight">{step.title}</h3>
                  <p className="mt-4 text-sm text-[color:var(--livinora-stone)] leading-relaxed">{step.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-5">
            <Link
              to="/konfigurator"
              className="btn-livinora bg-[color:var(--livinora-paper)] text-[color:var(--livinora-ink)] hover:bg-white"
              data-testid="process-cta-configurator"
            >
              Konfigurator starten <span>→</span>
            </Link>
            <Link
              to="/individualisierung"
              className="btn-livinora border border-[color:var(--livinora-stone)] text-[color:var(--livinora-paper)] hover:bg-[color:var(--livinora-paper)] hover:text-[color:var(--livinora-ink)]"
            >
              Persönliche Planung
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────────────── 05 RAUMSETS — SHOP TEASER ───────────────────── */}
      <section className="section-pad" data-testid="shop-teaser">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <SectionLabel index="05" text="Raumsets" />
            </Reveal>
            <Reveal delay={60}>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl mt-8 leading-[1.02] text-balance">
                Vorkonfigurierte Raumsets, anfragbar.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7">
                Ein kuratiertes Sortiment fertig komponierter Räume — Wohnzimmer, Schlafzimmer, Bad,
                Küche. Klar in Stilserie, Materialien und Komponenten. Mit Richtpreis zur Orientierung,
                Anfrage statt Checkout.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link to="/shop" className="btn-livinora btn-primary btn-magnetic" data-testid="shop-teaser-cta">
                  Raumsets ansehen <span>→</span>
                </Link>
                <Link to="/konfigurator" className="btn-line">
                  Komplettes Projekt
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal variant="mask" delay={80}>
              <Parallax speed={20}>
                <div className="media-wrap aspect-[4/3]">
                  <img
                    src={STYLE_SERIES[2].image}
                    alt="Vorkonfiguriertes Raumset — Skandi Wohnzimmer"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </Parallax>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-4 grid grid-cols-3 gap-x-6 gap-y-2 border-t border-[color:var(--livinora-hairline)] pt-4">
                <div>
                  <span className="micro-label">Sets</span>
                  <div className="serif text-2xl mt-1">20+</div>
                </div>
                <div>
                  <span className="micro-label">Stilserien</span>
                  <div className="serif text-2xl mt-1">05</div>
                </div>
                <div>
                  <span className="micro-label">Logik</span>
                  <div className="serif text-2xl mt-1">Anfrage</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────────── 06 REFIT ───────────────────── */}
      <section className="section-pad bg-[color:var(--livinora-paper-warm)] border-y border-[color:var(--livinora-hairline)]" data-testid="refit-teaser">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <SectionLabel index="06" text="Refit" />
            </Reveal>
            <Reveal delay={60}>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl mt-8 leading-[1.02] text-balance">
                Bestand, neu gelesen. Nicht neu verkleidet.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7">
                Viele Ferienobjekte verlieren nicht an Substanz, sondern an Ausstrahlung. Unsere
                Refit-Konzepte nehmen den Bestand ernst, erneuern gezielt — und heben das Objekt in
                eine spürbar höhere Qualität.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-y-3 text-sm">
                <li className="flex items-start gap-3"><span className="mt-[9px] w-3 h-px bg-[color:var(--livinora-ink)]" /> Vollständig oder modular</li>
                <li className="flex items-start gap-3"><span className="mt-[9px] w-3 h-px bg-[color:var(--livinora-ink)]" /> Mit oder ohne Bestand</li>
                <li className="flex items-start gap-3"><span className="mt-[9px] w-3 h-px bg-[color:var(--livinora-ink)]" /> Vor-Ort-Aufnahme möglich</li>
                <li className="flex items-start gap-3"><span className="mt-[9px] w-3 h-px bg-[color:var(--livinora-ink)]" /> Für Private & Betreiber</li>
              </ul>
            </Reveal>
            <Reveal delay={280}>
              <Link to="/refit" className="btn-livinora btn-primary btn-magnetic mt-10" data-testid="refit-teaser-cta">
                Refit-Ansatz ansehen <span>→</span>
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Reveal variant="scale" delay={80}>
              <Parallax speed={24}>
                <div className="media-wrap aspect-[5/6]">
                  <img src={IMAGES.refit} alt="Refit Interior" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </Parallax>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INDIVIDUALISIERUNG */}
      <section className="section-pad" data-testid="indiv-teaser">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <Reveal>
            <div className="card-depth p-10 md:p-12 h-full">
              <span className="micro-label">Vor-Ort-Planung</span>
              <h3 className="serif text-3xl md:text-4xl mt-5 leading-tight">Wenn Ihr Objekt eigene Regeln hat.</h3>
              <p className="mt-6 text-[color:var(--livinora-muted)] leading-relaxed">
                Für besondere Grundrisse, denkmalgeschützte Bauten oder größere Ambitionen kommt unser
                Planungsteam persönlich vor Ort. Wir messen auf, hören zu und übersetzen Ihr Objekt in
                ein schlüssiges Konzept.
              </p>
              <Link to="/individualisierung" className="btn-line mt-10" data-testid="indiv-vor-ort">
                Vor-Ort-Planung anfragen
              </Link>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="card-depth card-depth--warm p-10 md:p-12 h-full">
              <span className="micro-label">Konfigurator</span>
              <h3 className="serif text-3xl md:text-4xl mt-5 leading-tight">In wenigen Minuten zum Konzept.</h3>
              <p className="mt-6 text-[color:var(--livinora-muted)] leading-relaxed">
                Für alle, die Ihr Objekt selbstständig planen möchten: Objekttyp, Stilserie, Paket,
                Zusatzmodule — in einem eleganten, geführten Prozess. Anschließend senden Sie Ihre
                Anfrage oder speichern Ihr Konzept.
              </p>
              <Link to="/konfigurator" className="btn-line mt-10" data-testid="indiv-konfigurator">
                Konfigurator starten
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMMERCIAL */}
      <section className="section-pad border-t border-[color:var(--livinora-hairline)]" data-testid="commercial-teaser">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal variant="scale" delay={80}>
              <Parallax speed={20}>
                <div className="media-wrap aspect-[5/4]">
                  <img src={IMAGES.commercial} alt="Gewerbliche Lösungen" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </Parallax>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal>
              <SectionLabel index="07" text="Gewerblich" />
            </Reveal>
            <Reveal delay={60}>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl mt-8 leading-[1.02] text-balance">
                Eine Handschrift, viele Einheiten.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7">
                Für Ferienparks, Apartmentbetreiber und Projektentwickler übersetzen wir unser Konzept
                in ein skalierbares System — konsistent über Einheiten, differenziert in der Ausprägung,
                effizient in der Umsetzung.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-8 max-w-md">
                <div>
                  <div className="serif text-4xl leading-none">05</div>
                  <div className="micro-label mt-2.5">Stilserien</div>
                </div>
                <div>
                  <div className="serif text-4xl leading-none">03</div>
                  <div className="micro-label mt-2.5">Ausbaustufen</div>
                </div>
                <div>
                  <div className="serif text-4xl leading-none">∞</div>
                  <div className="micro-label mt-2.5">Einheiten</div>
                </div>
                <div>
                  <div className="serif text-4xl leading-none">01</div>
                  <div className="micro-label mt-2.5">Handschrift</div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={280}>
              <Link to="/gewerblich" className="btn-livinora btn-ghost mt-10" data-testid="commercial-teaser-cta">
                Gewerbliche Lösungen
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BROCHURE */}
      <section className="section-pad bg-[color:var(--livinora-paper-warm)] border-y border-[color:var(--livinora-hairline)]" data-testid="brochure-cta">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="micro-label">Broschüre</span>
            </Reveal>
            <Reveal delay={60}>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl mt-6 leading-[1.02] text-balance">
                Nehmen Sie Livinora mit.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7">
                Unsere Broschüre zeigt alle Stilserien, Pakete und Anwendungen auf einen Blick —
                ruhig, editorial, zum Durchblättern in Ihrem Tempo.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 flex flex-wrap gap-5">
                <Link to="/broschuere" className="btn-livinora btn-primary btn-magnetic" data-testid="brochure-cta-download">
                  Broschüre herunterladen <span>→</span>
                </Link>
                <Link to="/stilserien" className="btn-livinora btn-ghost">Stilserien ansehen</Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal variant="scale" delay={120}>
              <Parallax speed={18}>
                <div className="media-wrap aspect-[3/4]">
                  <img src={IMAGES.brochure} alt="Broschüre" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </Parallax>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" data-testid="faq">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="micro-label">FAQ</span>
            </Reveal>
            <Reveal delay={60}>
              <h2 className="serif text-4xl md:text-5xl mt-6 leading-[1.05]">
                Was häufig gefragt wird.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7">
                Weitere Fragen? Wir beantworten sie gerne in einem unverbindlichen Gespräch.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <Link to="/kontakt" className="btn-line mt-9" data-testid="faq-contact">
                Beratung anfragen
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.slice(0, 8).map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-[color:var(--livinora-hairline)]">
                  <AccordionTrigger className="text-left serif text-xl md:text-2xl py-6 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[color:var(--livinora-muted)] leading-relaxed text-base pb-8">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative pt-28 md:pt-40 pb-36 md:pb-48 overflow-hidden atmosphere-dark" data-testid="final-cta">
        <div className="absolute inset-0 -z-10">
          <Parallax speed={-40}>
            <img src={IMAGES.quietDetail} alt="" className="w-full h-[115%] object-cover opacity-[0.22]" />
          </Parallax>
          <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--livinora-paper)] via-[color:var(--livinora-paper)]/70 to-[color:var(--livinora-paper)]" />
        </div>
        <div className="container-livinora text-center">
          <Reveal>
            <span className="micro-label">Starten</span>
          </Reveal>
          <HeadlineMask delay={120} tag="h2">
            <span className="display-xl block text-5xl md:text-7xl lg:text-[96px] mt-8 text-balance max-w-5xl mx-auto">
              Lassen Sie uns Ihr Objekt zu Ende denken.
            </span>
          </HeadlineMask>
          <Reveal delay={240}>
            <p className="prose-livinora mt-8 mx-auto text-center">
              Eine Anfrage. Ein klarer Gesprächsrahmen. Ein belastbares Konzept.
            </p>
          </Reveal>
          <Reveal delay={340}>
            <div className="mt-14 flex flex-wrap justify-center gap-5">
              <Link to="/konfigurator" className="btn-livinora btn-primary btn-magnetic" data-testid="final-cta-inquiry">
                Projekt konfigurieren <span>→</span>
              </Link>
              <Link to="/kontakt" className="btn-livinora btn-ghost">Beratung anfragen</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
